/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { createAction } from 'utils/redux-utils';
import {
    CashBoxDetailsDto,
    BlockCashBoxQueryParams,
    UnblockCashBoxQueryParams,
    CashBoxesIntegrationStatusesParams,
    ChangeCashBoxStatusParams,
} from '../../api/cashBoxes/cashBoxesClient.typings';
import { Notification } from '../../../utils/utils';
import {
    TurnoversByPeriodEXCELParams,
    TurnoversByPeriodQueryParams,
    TurnoversByPeriodWithParams,
} from '../../api/apiCommon.typings';

export enum CashBoxDetailsActionType {
    OPEN_REGISTRY_CASHBOX_CARD_PAGE = 'CashBoxCard/ open page',
    EXIT_REGISTRY_CASHBOX_CARD_PAGE = 'CashBoxCard/ exit page',
    CHANGE_HISTORY_TAB = 'CashBoxCard/ change history tab',

    FETCH_CASHBOX_DETAILS_SUCCESS = 'CashBoxDetails/ fetch cashbox details success',
    CHANGE_CASHBOX_STATUS = 'CashBoxDetails/ change cashbox status',
    CHANGE_CASHBOX_STATUS_SUCCESS = 'CashBoxDetails/ change cashbox integration success',
    BLOCK_CASHBOX = 'CashBoxDetails/ block cashbox',
    BLOCK_CASHBOX_SUCCESS = 'CashBoxDetails/ block cashbox success',
    UNBLOCK_CASHBOX = 'CashBoxDetails/ unblock cashbox',
    UNBLOCK_CASHBOX_SUCCESS = 'CashBoxDetails/ unblock cashbox success',
    REMOVE_BLOCK_UNBLOCK_SCHEDULE = 'CashBoxDetails/ remove block-unblock schedule',
    REMOVE_BLOCK_UNBLOCK_SCHEDULE_SUCCESS = 'CashBoxDetails/ remove block-unblock schedule success',
    CHANGE_CASHBOX_INTEGRATION_STATUS = 'CashBoxDetails/ change cashbox integration status',
    CHANGE_CASHBOX_INTEGRATION_STATUS_SUCCESS = 'CashBoxDetails/ change cashbox integration status success',
    CASHBOX_DETAILS_ERROR = 'CashBoxDetails/ cashbox details error',

    FETCH_CASHBOX_TURNOVERS = 'CashBoxCard/ fetch cashbox turnovers',
    FETCH_CASHBOX_TURNOVERS_SUCCESS = 'CashBoxCard/ fetch cashbox turnovers success',
    FETCH_CASHBOX_TURNOVERS_ERROR = 'CashBoxCard/ fetch cashbox turnovers error',
    SET_TURNOVERS_HIDE_TOGGLE = 'CashBoxCard/ set turnovers table hide toggle',
    COLLAPSE_TURNOVERS = 'CashBoxCard/ collapse turnovers',
    EXPAND_TURNOVERS_PERIOD = 'CashBoxCard/ expand turnovers period',
    DOWNLOAD_TURNOVERS = 'CashBoxCard/ download turnovers as excel',
    DOWNLOAD_TURNOVERS_SUCCESS = 'CashBoxCard/ download turnovers as excel success',
    DOWNLOAD_TURNOVERS_ERROR = 'CashBoxCard/ download turnovers as excel error',
}

export const openRegistryCashBoxCardAction = (taxNumber: string) =>
    createAction(CashBoxDetailsActionType.OPEN_REGISTRY_CASHBOX_CARD_PAGE, taxNumber);

export const exitRegistryCashBoxCardAction = () =>
    createAction(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE);

export const changeHistoryTabAction = (value: number) =>
    createAction(CashBoxDetailsActionType.CHANGE_HISTORY_TAB, value);

// ------------------------ Cashbox details

export const fetchCashBoxDetailsSuccessAction = (payload: CashBoxDetailsDto) =>
    createAction(CashBoxDetailsActionType.FETCH_CASHBOX_DETAILS_SUCCESS, payload);

export const changeCashBoxStatusAction = (payload: ChangeCashBoxStatusParams) =>
    createAction(CashBoxDetailsActionType.CHANGE_CASHBOX_STATUS, payload);

export const changeCashBoxStatusSuccessAction = () =>
    createAction(CashBoxDetailsActionType.CHANGE_CASHBOX_STATUS_SUCCESS);

export const blockCashboxAction = (params: BlockCashBoxQueryParams) =>
    createAction(CashBoxDetailsActionType.BLOCK_CASHBOX, params);

export const blockCashboxSuccessAction = () => createAction(CashBoxDetailsActionType.BLOCK_CASHBOX_SUCCESS);

export const unblockCashboxAction = (params: UnblockCashBoxQueryParams) =>
    createAction(CashBoxDetailsActionType.UNBLOCK_CASHBOX, params);

export const unblockCashboxSuccessAction = () => createAction(CashBoxDetailsActionType.UNBLOCK_CASHBOX_SUCCESS);

export const removeBlockUnblockScheduleAction = () =>
    createAction(CashBoxDetailsActionType.REMOVE_BLOCK_UNBLOCK_SCHEDULE);

export const removeBlockUnblockScheduleSuccessAction = () =>
    createAction(CashBoxDetailsActionType.REMOVE_BLOCK_UNBLOCK_SCHEDULE_SUCCESS);

export const changeCashBoxIntegrationStatusAction = (payload: CashBoxesIntegrationStatusesParams) =>
    createAction(CashBoxDetailsActionType.CHANGE_CASHBOX_INTEGRATION_STATUS, payload);

export const changeCashBoxIntegrationStatusActionSuccess = () =>
    createAction(CashBoxDetailsActionType.CHANGE_CASHBOX_INTEGRATION_STATUS_SUCCESS);

export const cashBoxDetailsErrorAction = (notification: Notification) =>
    createAction(CashBoxDetailsActionType.CASHBOX_DETAILS_ERROR, { notification });

// ------------------------ Turnovers

export const fetchCashBoxTurnoversAction = (payload: TurnoversByPeriodQueryParams) =>
    createAction(CashBoxDetailsActionType.FETCH_CASHBOX_TURNOVERS, payload);

export const fetchCashBoxTurnoversSuccessAction = (payload: TurnoversByPeriodWithParams) =>
    createAction(CashBoxDetailsActionType.FETCH_CASHBOX_TURNOVERS_SUCCESS, payload);

export const fetchCashBoxTurnoversErrorAction = (notification: Notification) =>
    createAction(CashBoxDetailsActionType.FETCH_CASHBOX_TURNOVERS_ERROR, { notification });

export const setTurnoversHideToggleAction = (isToHide: boolean) =>
    createAction(CashBoxDetailsActionType.SET_TURNOVERS_HIDE_TOGGLE, isToHide);

export const collapseTurnoversAction = () => createAction(CashBoxDetailsActionType.COLLAPSE_TURNOVERS);

export const expandTurnoversPeriodAction = (payload: { period: string; isToHide: boolean }) =>
    createAction(CashBoxDetailsActionType.EXPAND_TURNOVERS_PERIOD, payload);

export const downloadTurnoversAction = (params: TurnoversByPeriodEXCELParams) =>
    createAction(CashBoxDetailsActionType.DOWNLOAD_TURNOVERS, params);

export const downloadTurnoversSuccessAction = () => createAction(CashBoxDetailsActionType.DOWNLOAD_TURNOVERS_SUCCESS);

export const downloadTurnoversErrorAction = (notification: Notification) =>
    createAction(CashBoxDetailsActionType.DOWNLOAD_TURNOVERS_ERROR, { notification });

export type CashBoxDetailsAction =
    | ReturnType<typeof openRegistryCashBoxCardAction>
    | ReturnType<typeof exitRegistryCashBoxCardAction>
    | ReturnType<typeof changeHistoryTabAction>
    | ReturnType<typeof fetchCashBoxDetailsSuccessAction>
    | ReturnType<typeof changeCashBoxStatusAction>
    | ReturnType<typeof changeCashBoxStatusSuccessAction>
    | ReturnType<typeof blockCashboxAction>
    | ReturnType<typeof blockCashboxSuccessAction>
    | ReturnType<typeof unblockCashboxAction>
    | ReturnType<typeof unblockCashboxSuccessAction>
    | ReturnType<typeof removeBlockUnblockScheduleAction>
    | ReturnType<typeof removeBlockUnblockScheduleSuccessAction>
    | ReturnType<typeof changeCashBoxIntegrationStatusAction>
    | ReturnType<typeof changeCashBoxIntegrationStatusActionSuccess>
    | ReturnType<typeof cashBoxDetailsErrorAction>
    | ReturnType<typeof fetchCashBoxTurnoversAction>
    | ReturnType<typeof fetchCashBoxTurnoversSuccessAction>
    | ReturnType<typeof fetchCashBoxTurnoversErrorAction>
    | ReturnType<typeof setTurnoversHideToggleAction>
    | ReturnType<typeof collapseTurnoversAction>
    | ReturnType<typeof expandTurnoversPeriodAction>
    | ReturnType<typeof downloadTurnoversAction>
    | ReturnType<typeof downloadTurnoversSuccessAction>
    | ReturnType<typeof downloadTurnoversErrorAction>;
